import React, { useCallback, useEffect, useState } from 'react';
import {
  dismissEventPlayguideLinkSuggestions,
  EventPlayguideLinkSuggestionGroup,
  listEventPlayguideLinkSuggestions,
} from '../../../api/playguides';
import { useConfirm } from '../../../design-system/ConfirmDialog';
import DsButton from '../../common/DsButton';
import { useToast } from '../../common/ToastSystem';
import { AutomaticTicketGuide } from './eventForm/AutomaticTicketSitePickerModal';
import '../../../css/admin/events/PlayguideLinkSuggestionNotice.css';

type Props = {
  eventUuid: string;
  canEdit: boolean;
  refreshKey?: number;
  onOpenSetup: (guideCode: AutomaticTicketGuide) => void;
};

type ToastApi = { error: (message: string) => void };

export default function PlayguideLinkSuggestionNotice({
  eventUuid,
  canEdit,
  refreshKey = 0,
  onOpenSetup,
}: Props) {
  const confirmDialog = useConfirm();
  const toast = useToast() as unknown as ToastApi;
  const [group, setGroup] = useState<EventPlayguideLinkSuggestionGroup | null>(null);
  const [dismissing, setDismissing] = useState(false);

  const load = useCallback(async () => {
    if (!canEdit || !eventUuid) {
      setGroup(null);
      return;
    }
    try {
      const events = await listEventPlayguideLinkSuggestions([eventUuid]);
      setGroup(events.find((item) => item.eventUuid === eventUuid) || null);
    } catch {
      // 背景で表示可否を確認する読み取り失敗は、ユーザー操作を妨げず無音にする。
      setGroup(null);
    }
  }, [canEdit, eventUuid]);

  useEffect(() => {
    let active = true;
    if (!canEdit || !eventUuid) {
      setGroup(null);
      return () => { active = false; };
    }
    void listEventPlayguideLinkSuggestions([eventUuid])
      .then((events) => {
        if (active) setGroup(events.find((item) => item.eventUuid === eventUuid) || null);
      })
      .catch(() => {
        if (active) setGroup(null);
      });
    return () => { active = false; };
  }, [canEdit, eventUuid, refreshKey]);

  const suggestions = group?.suggestions || [];
  const primary = suggestions[0];
  if (!primary) return null;

  const dismiss = async () => {
    const confirmed = await confirmDialog({
      message: 'このイベントについて、現在の券売連動候補のお知らせを今後表示しないようにしますか？',
      danger: false,
    });
    if (!confirmed) return;
    setDismissing(true);
    try {
      await dismissEventPlayguideLinkSuggestions(
        eventUuid,
        suggestions.map((suggestion) => suggestion.externalPerformanceId)
      );
      setGroup(null);
    } catch (error: any) {
      toast.error(error?.message || '券売連動候補のお知らせを非表示にできませんでした。');
      await load();
    } finally {
      setDismissing(false);
    }
  };

  return (
    <section className="playguide-link-suggestion-notice" aria-labelledby="playguide-link-suggestion-title">
      <div className="playguide-link-suggestion-notice__icon" aria-hidden="true">
        <i className="fa-solid fa-circle-exclamation" />
      </div>
      <div className="playguide-link-suggestion-notice__content">
        <p className="playguide-link-suggestion-notice__eyebrow">券売連動候補</p>
        <h2 id="playguide-link-suggestion-title">取得済みの券売情報がこのイベントの可能性があります</h2>
        <p>
          {primary.guideLabel}の「{primary.eventName}」が、
          Casticartの「{primary.casticartEventName}」
          {primary.casticartPerformanceName ? ` / ${primary.casticartPerformanceName}` : ''}
          と公演日時・タイトルから一致候補になっています。
          内容と券種を確認してから連動してください。
        </p>
        {primary.performanceSelectionRequired ? (
          <small>同日に複数公演があります。連動設定で対象公演を選択してください。</small>
        ) : null}
        {suggestions.length > 1 ? (
          <small>ほかに{suggestions.length - 1}件の候補があります。連動設定で確認できます。</small>
        ) : null}
      </div>
      <div className="playguide-link-suggestion-notice__actions">
        <DsButton onClick={() => onOpenSetup(primary.guideCode as AutomaticTicketGuide)}>
          連動設定を開く
        </DsButton>
        <DsButton variant="ghost" loading={dismissing} onClick={() => void dismiss()}>
          表示しない
        </DsButton>
      </div>
    </section>
  );
}
